import React from "react";
import {Badge, Card, CardBody, Col, Row} from "reactstrap";

const BuyerLeadEngagement = ({
    supportCases,
    hasSupportData,
    totalCases,
    openCases,
    resolvedCases,
    formatDate,
    formatDateTime,
    categoryInterests,
    sentimentValue,
    feedbackCount,
    emailsReceived,
    emailOpenRate,
    smsSent,
    smsDeliveryRate,
    acquisitionSource,
    acquisitionDate,
    averageRating,
    reviewCount,
    ugcContributions,
    socialConnections,
    preferredChannel,
    bestContactTime,
    aiTotalQuestions,
    aiLast30Days,
    aiLifetimeCost,
    aiImagesGenerated,
    formatCurrency,
    aiToolUsage,
    aiCostBreakdown,
    aiGeneratedImages,
    internalNotes,
    auditLog,
}) => {
    const cases = supportCases || [];
    const interests = categoryInterests || [];
    const socials = socialConnections || [];
    const tools = aiToolUsage || [];
    const costs = aiCostBreakdown || [];
    const images = aiGeneratedImages || [];
    const notes = internalNotes || [];
    const audits = auditLog || [];

    return (
        <>
            <Row>
                <Col sm="12">
                    <Card id="buyer-lead-section-support" className="buyer-lead-support-card buyer-lead-section">
                        <div className="buyer-lead-support-header">
                            <div>
                                <div className="buyer-lead-support-title">
                                    <i className="fa fa-headset mr-2"></i> Support Cases
                                </div>
                                <div className="buyer-lead-support-subtitle">
                                    Customer support history, open cases and resolution tracking
                                </div>
                            </div>
                        </div>
                        <CardBody>
                            <Row className="mb-4">
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-support-metric metric-blue">
                                        <div className="buyer-lead-support-metric-value">
                                            {hasSupportData ? totalCases : "-"}
                                        </div>
                                        <div className="buyer-lead-support-metric-label">Total Cases Raised</div>
                                    </div>
                                </Col>
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-support-metric metric-orange">
                                        <div className="buyer-lead-support-metric-value">
                                            {hasSupportData ? openCases : "-"}
                                        </div>
                                        <div className="buyer-lead-support-metric-label">Open Cases</div>
                                    </div>
                                </Col>
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-support-metric metric-green">
                                        <div className="buyer-lead-support-metric-value">
                                            {hasSupportData ? resolvedCases : "-"}
                                        </div>
                                        <div className="buyer-lead-support-metric-label">Resolved Cases</div>
                                    </div>
                                </Col>
                            </Row>

                            <div className="buyer-lead-table-wrapper">
                                <table className="buyer-lead-table">
                                    <thead>
                                        <tr>
                                            <th>Case ID</th>
                                            <th>Date</th>
                                            <th>Category</th>
                                            <th>Priority</th>
                                            <th>Status</th>
                                            <th>Agent</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cases.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="buyer-lead-empty-row">
                                                    No support cases available.
                                                </td>
                                            </tr>
                                        ) : (
                                            cases.map((item, index) => (
                                                <tr key={`case-${index}`}>
                                                    <td>{item.case_id || item.id || "-"}</td>
                                                    <td>{formatDate(item.date || item.created_at)}</td>
                                                    <td>{item.category || item.type || "-"}</td>
                                                    <td>
                                                        <span className="buyer-lead-pill">{item.priority || "-"}</span>
                                                    </td>
                                                    <td>
                                                        <span className="buyer-lead-pill">{item.status || "-"}</span>
                                                    </td>
                                                    <td>{item.agent || item.assignee || "-"}</td>
                                                    <td>
                                                        <button type="button" className="btn btn-link btn-sm buyer-lead-link">
                                                            View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col sm="12">
                    <Card id="buyer-lead-section-feedback" className="buyer-lead-feedback-card buyer-lead-section">
                        <div className="buyer-lead-feedback-header">
                            <div>
                                <div className="buyer-lead-feedback-title">
                                    <i className="fa fa-star mr-2"></i> Feedback & Interests
                                </div>
                                <div className="buyer-lead-feedback-subtitle">
                                    Customer feedback, category interests and sentiment analysis
                                </div>
                            </div>
                        </div>
                        <CardBody>
                            <Row>
                                <Col lg="7" className="mb-3">
                                    <div className="buyer-lead-subsection-card">
                                        <div className="buyer-lead-table-title">Category Interests</div>
                                        {interests.length === 0 ? (
                                            <div className="text-muted">No category interests available.</div>
                                        ) : (
                                            <div className="buyer-lead-feedback-tags">
                                                {interests.map((item, index) => (
                                                    <Badge key={`interest-${index}`} color="light" className="mr-2 mb-2">
                                                        {item.label || item.name || "-"}
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                                <Col lg="5" className="mb-3">
                                    <div className="buyer-lead-sentiment-card">
                                        <div className="buyer-lead-table-title">Overall Sentiment</div>
                                        <div className="buyer-lead-sentiment-value">{sentimentValue}</div>
                                        <div className="buyer-lead-sentiment-sub">
                                            {feedbackCount !== null
                                                ? `Based on ${feedbackCount} feedback entries and support interactions`
                                                : "Sentiment data unavailable"}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col sm="12">
                    <Card id="buyer-lead-section-marketing" className="buyer-lead-marketing-card buyer-lead-section">
                        <div className="buyer-lead-marketing-header">
                            <div>
                                <div className="buyer-lead-marketing-title">
                                    <i className="fa fa-bullhorn mr-2"></i> Marketing History
                                </div>
                                <div className="buyer-lead-marketing-subtitle">
                                    All marketing touchpoints, campaign engagement and attribution
                                </div>
                            </div>
                        </div>
                        <CardBody>
                            <Row>
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-marketing-stat">
                                        <div className="buyer-lead-marketing-stat-header">
                                            <span>Emails Received</span>
                                            <i className="fa fa-envelope"></i>
                                        </div>
                                        <div className="buyer-lead-marketing-stat-value">{emailsReceived ?? "-"}</div>
                                        <div className="buyer-lead-marketing-stat-sub">
                                            Open rate: {emailOpenRate !== null ? `${emailOpenRate}%` : "-"}
                                        </div>
                                    </div>
                                </Col>
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-marketing-stat">
                                        <div className="buyer-lead-marketing-stat-header">
                                            <span>SMS Sent</span>
                                            <i className="fa fa-comment"></i>
                                        </div>
                                        <div className="buyer-lead-marketing-stat-value">{smsSent ?? "-"}</div>
                                        <div className="buyer-lead-marketing-stat-sub">
                                            Delivery rate: {smsDeliveryRate !== null ? `${smsDeliveryRate}%` : "-"}
                                        </div>
                                    </div>
                                </Col>
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-marketing-stat">
                                        <div className="buyer-lead-marketing-stat-header">
                                            <span>Acquisition Source</span>
                                            <i className="fa fa-chart-line"></i>
                                        </div>
                                        <div className="buyer-lead-marketing-stat-value">{acquisitionSource}</div>
                                        <div className="buyer-lead-marketing-stat-sub">
                                            {acquisitionDate ? formatDate(acquisitionDate) : "-"}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col sm="12">
                    <Card id="buyer-lead-section-reviews" className="buyer-lead-reviews-card buyer-lead-section">
                        <div className="buyer-lead-reviews-header">
                            <div>
                                <div className="buyer-lead-reviews-title">
                                    <i className="fa fa-comment mr-2"></i> Reviews & User Generated Content
                                </div>
                                <div className="buyer-lead-reviews-subtitle">
                                    Product reviews, ratings and UGC submissions
                                </div>
                            </div>
                        </div>
                        <CardBody>
                            <Row>
                                <Col lg="6" className="mb-3">
                                    <div className="buyer-lead-review-metric metric-amber">
                                        <div className="buyer-lead-review-label">Average Rating</div>
                                        <div className="buyer-lead-review-value">
                                            {averageRating ?? "-"} <span>★</span>
                                        </div>
                                        <div className="buyer-lead-review-sub">
                                            {reviewCount !== null
                                                ? `From ${reviewCount} reviews`
                                                : "No review data available"}
                                        </div>
                                    </div>
                                </Col>
                                <Col lg="6" className="mb-3">
                                    <div className="buyer-lead-review-metric metric-pink">
                                        <div className="buyer-lead-review-label">UGC Contributions</div>
                                        <div className="buyer-lead-review-value">{ugcContributions ?? "-"}</div>
                                        <div className="buyer-lead-review-sub">Photos uploaded</div>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col sm="12">
                    <Card id="buyer-lead-section-social" className="buyer-lead-social-card buyer-lead-section">
                        <div className="buyer-lead-social-header">
                            <div>
                                <div className="buyer-lead-social-title">
                                    <i className="fa fa-instagram mr-2"></i> Social Connections
                                </div>
                                <div className="buyer-lead-social-subtitle">
                                    Social media presence and preferred contact channels
                                </div>
                            </div>
                        </div>
                        <CardBody>
                            <Row>
                                <Col lg="6" className="mb-3">
                                    <div className="buyer-lead-subsection-card">
                                        <div className="buyer-lead-table-title">Connected Accounts</div>
                                        {socials.length === 0 ? (
                                            <div className="text-muted">No connected accounts.</div>
                                        ) : (
                                            socials.map((account, index) => (
                                                <div className="buyer-lead-social-account" key={`social-${index}`}>
                                                    <div>
                                                        <div className="buyer-lead-social-name">
                                                            {account.platform || account.name || "-"}
                                                        </div>
                                                        <div className="buyer-lead-social-handle">
                                                            {account.handle || account.username || "-"}
                                                        </div>
                                                    </div>
                                                    {account.status && <Badge color="success">{account.status}</Badge>}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </Col>
                                <Col lg="6" className="mb-3">
                                    <div className="buyer-lead-subsection-card">
                                        <div className="buyer-lead-table-title">Contact Preferences</div>
                                        <div className="buyer-lead-contact-row">
                                            <span>Preferred Channel</span>
                                            <Badge color="primary">{preferredChannel}</Badge>
                                        </div>
                                        <div className="buyer-lead-contact-row">
                                            <span>Best Time</span>
                                            <span>{bestContactTime}</span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col sm="12">
                    <Card id="buyer-lead-section-ai" className="buyer-lead-ai-card buyer-lead-section">
                        <div className="buyer-lead-ai-header">
                            <div>
                                <div className="buyer-lead-ai-header-title">
                                    <i className="fa fa-robot mr-2"></i> AI Usage & Cost Tracking
                                </div>
                                <div className="buyer-lead-ai-header-subtitle">
                                    AI tool interactions, generated content and associated costs
                                </div>
                            </div>
                        </div>
                        <CardBody>
                            <Row className="mb-4">
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-metric-card metric-purple">
                                        <div className="buyer-lead-metric-value">{aiTotalQuestions ?? "-"}</div>
                                        <div className="buyer-lead-metric-label">Total AI Questions</div>
                                    </div>
                                </Col>
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-metric-card metric-blue">
                                        <div className="buyer-lead-metric-value">{aiLast30Days ?? "-"}</div>
                                        <div className="buyer-lead-metric-label">Last 30 Days</div>
                                    </div>
                                </Col>
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-metric-card metric-green">
                                        <div className="buyer-lead-metric-value">{formatCurrency(aiLifetimeCost)}</div>
                                        <div className="buyer-lead-metric-label">Lifetime AI Cost</div>
                                    </div>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col lg="6" className="mb-3">
                                    <div className="buyer-lead-subsection-card">
                                        <div className="buyer-lead-table-title">AI Tool Usage</div>
                                        {tools.length === 0 ? (
                                            <div className="text-muted">No AI tool usage data.</div>
                                        ) : (
                                            tools.map((tool, index) => (
                                                <div className="buyer-lead-recent-row" key={`tool-${index}`}>
                                                    <div className="buyer-lead-recent-info">
                                                        <div className="buyer-lead-recent-title">
                                                            {tool.name || tool.tool || "-"}
                                                        </div>
                                                    </div>
                                                    <div className="buyer-lead-recent-value">
                                                        {tool.sessions ?? tool.count ?? "-"} sessions
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </Col>
                                <Col lg="6" className="mb-3">
                                    <div className="buyer-lead-subsection-card">
                                        <div className="buyer-lead-table-title">Cost Breakdown</div>
                                        {costs.length === 0 ? (
                                            <div className="text-muted">No cost breakdown data.</div>
                                        ) : (
                                            costs.map((item, index) => (
                                                <div className="buyer-lead-recent-row" key={`cost-${index}`}>
                                                    <div className="buyer-lead-recent-info">
                                                        <div className="buyer-lead-recent-title">
                                                            {item.label || item.name || "-"}
                                                        </div>
                                                    </div>
                                                    <div className="buyer-lead-recent-value">
                                                        {formatCurrency(item.amount)}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            <div className="buyer-lead-table-title">AI Generated Images</div>
                            <Row>
                                {images.length === 0 ? (
                                    <Col sm="12">
                                        <div className="text-muted">No AI generated images.</div>
                                    </Col>
                                ) : (
                                    images.map((image, index) => (
                                        <Col lg="3" md="6" sm="12" className="mb-3" key={`ai-img-${index}`}>
                                            <div className="buyer-lead-wishlist-item">
                                                <div className="buyer-lead-wishlist-image">
                                                    {image.url ? (
                                                        <img src={image.url} alt={image.title || "AI image"} />
                                                    ) : (
                                                        <div className="buyer-lead-wishlist-placeholder">
                                                            <i className="fa fa-image"></i>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="buyer-lead-wishlist-body">
                                                    <div className="buyer-lead-wishlist-title">{image.title || "-"}</div>
                                                    <div className="buyer-lead-recent-sub">
                                                        {formatDate(image.created_at || image.date)}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    ))
                                )}
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col sm="12">
                    <Card id="buyer-lead-section-notes" className="buyer-lead-notes-card buyer-lead-section">
                        <div className="buyer-lead-notes-header">
                            <div>
                                <div className="buyer-lead-notes-title">
                                    <i className="fa fa-sticky-note mr-2"></i> Notes & Audit Log
                                </div>
                                <div className="buyer-lead-notes-subtitle">
                                    Internal notes and complete audit trail of customer data changes
                                </div>
                            </div>
                        </div>
                        <CardBody>
                            <Row>
                                <Col lg="6" className="mb-3">
                                    <div className="buyer-lead-subsection-card">
                                        <div className="buyer-lead-notes-toolbar">
                                            <div className="buyer-lead-table-title">Internal Notes</div>
                                            <button type="button" className="btn btn-primary btn-sm">
                                                Add Note
                                            </button>
                                        </div>
                                        {notes.length === 0 ? (
                                            <div className="text-muted">No internal notes.</div>
                                        ) : (
                                            notes.map((note, index) => (
                                                <div className="buyer-lead-note-card" key={`note-${index}`}>
                                                    <div className="buyer-lead-note-header">
                                                        <div className="buyer-lead-note-author">
                                                            {note.author || note.user || "-"}
                                                        </div>
                                                        <div className="buyer-lead-note-date">
                                                            {formatDateTime(note.date || note.created_at)}
                                                        </div>
                                                    </div>
                                                    <div className="buyer-lead-note-body">
                                                        {note.text || note.message || "-"}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </Col>
                                <Col lg="6" className="mb-3">
                                    <div className="buyer-lead-subsection-card">
                                        <div className="buyer-lead-table-title">Audit Log</div>
                                        {audits.length === 0 ? (
                                            <div className="text-muted">No audit log entries.</div>
                                        ) : (
                                            audits.map((entry, index) => (
                                                <div className="buyer-lead-audit-card" key={`audit-${index}`}>
                                                    <div className="buyer-lead-audit-title">
                                                        {entry.title || entry.action || "-"}
                                                    </div>
                                                    <div className="buyer-lead-audit-sub">
                                                        {entry.description || entry.detail || "-"}
                                                    </div>
                                                    <div className="buyer-lead-audit-meta">
                                                        {formatDate(entry.date || entry.created_at)} •{" "}
                                                        {entry.source || entry.user || "-"}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default BuyerLeadEngagement;
